import {
  confirmFederatedLinkSchema,
  type ConfirmFederatedLinkInput,
  type FederatedProviderSlug,
} from '@instanexus/contracts';
import { errorMessage, useTranslation } from '@instanexus/i18n';
import { isApiError } from '@instanexus/sdk';
import {
  Button,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@instanexus/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState, type ReactElement } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { Link } from 'react-router';

import { useApiClient } from '@/hooks/use-api-client';
import { FEDERATED_RESTART_CODE } from '@/lib/federated-restart';

const UNEXPECTED_FAILURE = 'UNEXPECTED';

export interface FederatedConfirmFormProps {
  readonly provider: FederatedProviderSlug;
  /** Where "Start over" goes: the page the person started from. */
  readonly startOverTo: string;
  /** The API linked the account and returned the central session's access token. */
  onConfirmed(accessToken: string): void;
  /** FEDERATED_SIGN_IN_RESTART: start the provider round trip once more. Resolves false when it could not. */
  onRestart(): Promise<boolean>;
}

/**
 * "Confirm it's you" (ADR 0040 decision 6(3)): the emailed six-digit code that links a provider
 * account to the existing InstaCheckin account. Submit and resend report inline; neither is a page load.
 */
export function FederatedConfirmForm({
  provider,
  startOverTo,
  onConfirmed,
  onRestart,
}: FederatedConfirmFormProps): ReactElement {
  const { t } = useTranslation('auth');
  const api = useApiClient();
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const validationMessage = errorMessage(t, 'VALIDATION_FAILED');
  const resolver = useMemo<Resolver<ConfirmFederatedLinkInput>>(
    () =>
      zodResolver(confirmFederatedLinkSchema, {
        path: [],
        async: true,
        errorMap: () => ({ message: validationMessage }),
      }),
    [validationMessage],
  );
  const form = useForm<ConfirmFederatedLinkInput>({ resolver, defaultValues: { code: '' } });

  const onSubmit = form.handleSubmit(async (values: ConfirmFederatedLinkInput): Promise<void> => {
    setErrorCode(null);
    setResent(false);
    try {
      const session = await api.federation.confirm(provider, { code: values.code.trim() });
      onConfirmed(session.accessToken);
    } catch (error: unknown) {
      const code = isApiError(error) ? error.code : UNEXPECTED_FAILURE;
      if (code === FEDERATED_RESTART_CODE) {
        try {
          if (await onRestart()) return;
        } catch (restartError: unknown) {
          setErrorCode(isApiError(restartError) ? restartError.code : UNEXPECTED_FAILURE);
          return;
        }
      }
      setErrorCode(code);
    }
  });

  const resend = async (): Promise<void> => {
    setErrorCode(null);
    setResent(false);
    setIsResending(true);
    try {
      await api.federation.resendConfirmation(provider);
      setResent(true);
    } catch (error: unknown) {
      setErrorCode(isApiError(error) ? error.code : UNEXPECTED_FAILURE);
    } finally {
      setIsResending(false);
    }
  };

  const isSubmitting = form.formState.isSubmitting;

  return (
    <>
      <CardHeader>
        <h1 className="text-3xl font-semibold text-primary text-balance">
          {t('federated.confirm.title')}
        </h1>
        <CardDescription>{t('federated.confirm.description')}</CardDescription>
      </CardHeader>
      <Form {...form}>
        <form
          noValidate
          onSubmit={(event) => {
            void onSubmit(event);
          }}
        >
          <CardContent className="flex flex-col gap-4">
            {errorCode === null ? null : (
              <p role="alert" className="text-danger text-sm text-pretty">
                {errorMessage(t, errorCode)}
              </p>
            )}
            {resent ? (
              <p role="status" className="text-muted-foreground text-sm text-pretty">
                {t('federated.confirm.resent')}
              </p>
            ) : null}
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('federated.confirm.codeLabel')}</FormLabel>
                  <FormControl>
                    <Input
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      autoCapitalize="none"
                      spellCheck={false}
                      maxLength={6}
                      width="sm"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
          <CardFooter className="flex flex-col items-stretch gap-3">
            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? t('federated.confirm.submitting') : t('federated.confirm.submit')}
            </Button>
            <Button
              type="button"
              variant="link"
              className="w-full"
              disabled={isResending}
              aria-busy={isResending}
              onClick={() => {
                void resend();
              }}
            >
              {isResending ? t('federated.confirm.resending') : t('federated.confirm.resend')}
            </Button>
            <Button asChild variant="link" className="w-full">
              <Link to={startOverTo}>{t('federated.confirm.startOver')}</Link>
            </Button>
          </CardFooter>
        </form>
      </Form>
    </>
  );
}
